/* eslint-disable max-lines */
import { Card, CardHeader, CardBody, Container, Row, Col, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import { useContext, useState, useEffect, useMemo } from 'react';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import CommonHeader from '../../../components/Headers/CommonHeader';
import SupportService from '../../../services/Support';
import SupportListInterface, { statusEnum } from '../../../interfaces/SupportListInterface';
import { PagedMetaInterface, createPagedMeta } from '../../../entities/PagedMeta';
import { OptionsPagedInterface, createOptionsPage } from '../../../entities/OptionsPaged';
import DepartmentModal from './DepartmentModal';
import { FilterInterface, FilteredOptionsInterface } from '../../../entities/FilteredOptions';
import isAdminHelper from '../../../helpers/isAdmin';
import isUpperManagement from '../../../helpers/isUpperManagement';
import isDirector from '../../../helpers/isDirector';
import isEducationDirectorAdminAssistant from '../../../helpers/isEducationDirectorAdminAssistant';
import isFinanceUser from '../../../helpers/isFinanceUser';
import isHRUser from '../../../helpers/isHRUser';
import DataTable from '../../../components/Core/DataTable';
import buildSupportListColumns from './datatableCollumns/SupportListColumns';
import { SUPPORT_DEPARTMENTS } from './taxonomies/departments';
import { useFranchiseeTrelloIntegration } from '../../../hooks/useFranchiseeTrelloIntegration';
import SchoolService from '../../../services/School';
import FilterBar, { ActiveFilter, EnumOption, FilterFieldDef } from '../Staff/DirectoryList/DirectoryFilters';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';

type SupportTab = 'active' | 'closed';

const PRIORITY_OPTIONS: EnumOption[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

const PRIORITY_FIELD: FilterFieldDef = {
  key: 'priority',
  label: 'Priority',
  type: 'enum',
  icon: 'fa-flag',
  backendField: 'priority',
};

const DEPARTMENT_FIELD: FilterFieldDef = {
  key: 'department',
  label: 'Department',
  type: 'enum',
  icon: 'fa-sitemap',
  backendField: 'department',
};

const SCHOOL_FIELD: FilterFieldDef = {
  key: 'school',
  label: 'School',
  type: 'enum',
  icon: 'fa-school',
  backendField: 'schoolId',
};

const SupportList = () => {
  const { token, currentSchool, user }: any = useContext(AuthContext);
  const { isTrelloActive, integrationChecked } = useFranchiseeTrelloIntegration();
  const { isMobile } = useCheckMobileScreen();
  const [tab, setTab] = useState<SupportTab>('active');
  const [countStatus, setCountStatus] = useState({
    OPEN: { total: 0 },
    PENDING: { total: 0 },
    CLOSED: { total: 0 },
  });

  const [data, setData] = useState([] as any[]);
  const [pagedMeta, setPagedMeta] = useState(createPagedMeta());
  const [filteredOptions, setFilteredOptions] = useState({
    filters: [],
    options: createOptionsPage(pagedMeta),
  } as { filters: FilterInterface[]; options: OptionsPagedInterface });
  const [isDiscountsOpen, setIsDiscountsOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [schoolList, setSchoolList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const canSelectSchools = isAdminHelper(user) || user?.employee?.canCreateSupportTicketsWithOtherSchools;
  const filterFields = canSelectSchools
    ? [SCHOOL_FIELD, PRIORITY_FIELD, DEPARTMENT_FIELD]
    : [PRIORITY_FIELD, DEPARTMENT_FIELD];

  const activeTicketsCount = Number(countStatus[statusEnum.OPEN].total) + Number(countStatus[statusEnum.PENDING].total);

  const isAdmin = (user: any) => {
    return (
      isAdminHelper(user) ||
      isHRUser(user) ||
      isUpperManagement(user) ||
      isDirector(user) ||
      isEducationDirectorAdminAssistant(user) ||
      isFinanceUser(user)
    );
  };

  useEffect(() => {
    loadList(filteredOptions);
    loadCountByStatus();
  }, [filteredOptions, tab, activeFilters]);

  useEffect(() => {
    if (!canSelectSchools || !currentSchool?.value) return;
    setActiveFilters((prev) => [
      { key: 'school', value: [currentSchool.value] },
      ...prev.filter((filter) => filter.key !== 'school'),
    ]);
  }, [currentSchool]);

  useEffect(() => {
    if (canSelectSchools) {
      loadSchools();
    }
  }, []);

  const loadSchools = async () => {
    try {
      const result = await SchoolService(token || '').list({
        filters: [],
        options: createOptionsPage(createPagedMeta({ perPage: 999999 })),
      });
      const schools = (result.data || [])
        .map((school: any) => ({ id: school.id, name: school.name }))
        .sort((a: any, b: any) => a.name.localeCompare(b.name));
      setSchoolList(schools);
    } catch (e: any) {
      setSchoolList([]);
    }
  };

  const selectedSchoolIds = (): string[] => {
    if (!canSelectSchools) {
      return currentSchool?.value ? [currentSchool.value] : [];
    }
    return (activeFilters.find((filter) => filter.key === 'school')?.value as string[]) || [];
  };

  const optionsFor = (key: string): EnumOption[] => {
    if (key === 'priority') return PRIORITY_OPTIONS;
    if (key === 'department') return SUPPORT_DEPARTMENTS.map((d) => ({ value: d.id, label: d.label }));
    return schoolList.map((school: any) => ({ value: school.id, label: school.name }));
  };

  const loadCountByStatus = async () => {
    const result = await SupportService(token || '').countByStatus(selectedSchoolIds());
    const toInt = (v: unknown) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    setCountStatus({
      OPEN: { total: toInt(result.data.find((item: any) => item.status === 'OPEN')?.total) },
      PENDING: { total: toInt(result.data.find((item: any) => item.status === 'PENDING')?.total) },
      CLOSED: { total: toInt(result.data.find((item: any) => item.status === 'CLOSED')?.total) },
    });
  };

  const loadList = async (filteredOptions: FilteredOptionsInterface) => {
    setLoading(true);
    try {
      let result: any = {};
      const currentOptions = filteredOptions.options || createOptionsPage(pagedMeta);
      const normalizedMeta = {
        ...pagedMeta,
        currentPage: currentOptions.page || pagedMeta.currentPage,
        perPage: currentOptions.itemsPerPage || pagedMeta.perPage,
      };

      const filters: FilterInterface[] = [];
      const schoolIds = selectedSchoolIds();
      if (schoolIds.length > 0) {
        filters.push({
          field: 'schoolId',
          operation: 'in',
          value: schoolIds,
        });
      }
      if (tab === 'active') {
        filters.push({
          field: 'status',
          operation: 'in',
          value: ['OPEN', 'PENDING'],
        });
      } else {
        filters.push({
          field: 'status',
          operation: '=',
          value: 'CLOSED',
        });
      }
      activeFilters
        .filter((filter) => filter.key !== 'school')
        .forEach((filter) => {
          const field = filterFields.find((definition) => definition.key === filter.key)?.backendField;
          const values = (filter.value as string[]) || [];
          if (field && values.length > 0) {
            filters.push({
              field,
              operation: 'in',
              value: values,
            });
          }
        });

      filteredOptions.filters = filters;
      filteredOptions.options = createOptionsPage(normalizedMeta, { sortBy: ['createdAt'], sortDesc: [true] });

      result = await SupportService(token || '').list(filteredOptions);
      const data: SupportListInterface[] = result.data;
      setData(data);
      const meta: PagedMetaInterface = result.meta;
      if (meta) {
        setPagedMeta(meta);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (next: SupportTab) => {
    setTab(next);
    setFilteredOptions((prev) => ({
      filters: prev.filters,
      options: createOptionsPage(
        {
          currentPage: 1,
          perPage: prev.options?.itemsPerPage || pagedMeta.perPage,
        } as PagedMetaInterface,
        { sortBy: ['createdAt'], sortDesc: [true] },
      ),
    }));
  };

  const toggleModalDiscounts = () => {
    setIsDiscountsOpen(!isDiscountsOpen);
  };

  const handlePerRowsChange = async (perPage: number) => {
    const newOptions = {
      ...pagedMeta,
      perPage,
    };
    const newFilteredOptions = {
      filters: filteredOptions.filters,
      options: createOptionsPage(newOptions),
    };
    setFilteredOptions(newFilteredOptions);
  };

  const handlePageChange = async (page: number) => {
    const newOptions = {
      ...pagedMeta,
      currentPage: page,
    };
    const newFilteredOptions = {
      filters: filteredOptions.filters,
      options: createOptionsPage(newOptions),
    };
    setFilteredOptions(newFilteredOptions);
  };

  const columns = useMemo(() => buildSupportListColumns(canSelectSchools), [canSelectSchools]);

  return (
    <>
      <CommonHeader />
      <Container className="mt--8" fluid>
        <Row>
          <Col className="order-xl-2" xl="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="12" md="6">
                    <h3 className="mb-0">Support Tickets</h3>
                    <p className="mb-2 text-muted">Manage and track all support requests</p>
                  </Col>
                  <Col className="text-right mt-3 mt-md-0" xs="12" md="6">
                    {isAdmin(user) && integrationChecked && !isTrelloActive && (
                      <Button onClick={toggleModalDiscounts}>
                        <i className="fas fa-cog"></i>
                      </Button>
                    )}
                    <Link to={'/support/new'}>
                      <Button color="primary" className="float-right">
                        Create New
                      </Button>
                    </Link>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col xs="12">
                    <div
                      className="d-inline-flex align-items-center p-1"
                      role="tablist"
                      style={{
                        backgroundColor: '#e8ecf1',
                        borderRadius: 999,
                        gap: 0,
                      }}
                    >
                      <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'active'}
                        className={`border-0 ${tab === 'active' ? 'bg-primary text-white' : 'text-dark'}`}
                        onClick={() => handleTabChange('active')}
                        style={{
                          borderRadius: 999,
                          padding: isMobile ? '0.45rem 0.85rem' : '0.45rem 1.15rem',
                          fontSize: isMobile ? '0.8rem' : '0.875rem',
                          lineHeight: 1.35,
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          backgroundColor: tab === 'active' ? undefined : 'transparent',
                          boxShadow: tab === 'active' ? '0 1px 4px rgba(94, 114, 228, 0.45)' : 'none',
                          transition: 'background-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease',
                        }}
                      >
                        Active Tickets ({activeTicketsCount})
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={tab === 'closed'}
                        className={`border-0 ${tab === 'closed' ? 'bg-primary text-white' : 'text-dark'}`}
                        onClick={() => handleTabChange('closed')}
                        style={{
                          borderRadius: 999,
                          padding: isMobile ? '0.45rem 0.85rem' : '0.45rem 1.15rem',
                          fontSize: isMobile ? '0.8rem' : '0.875rem',
                          lineHeight: 1.35,
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          backgroundColor: tab === 'closed' ? undefined : 'transparent',
                          boxShadow: tab === 'closed' ? '0 1px 4px rgba(94, 114, 228, 0.45)' : 'none',
                          transition: 'background-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease',
                        }}
                      >
                        Closed Tickets ({countStatus[statusEnum.CLOSED].total})
                      </button>
                    </div>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col xs="12">
                    <FilterBar
                      fields={filterFields}
                      filters={activeFilters}
                      onChange={setActiveFilters}
                      optionsFor={optionsFor}
                    />
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <DataTable
                  header={false}
                  columns={columns}
                  data={data}
                  totalRows={pagedMeta.total}
                  loading={loading}
                  button={false}
                  onChangeRowsPerPage={handlePerRowsChange}
                  onChangePage={handlePageChange}
                  shadowStyle={false}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <DepartmentModal isOpen={isDiscountsOpen} toggleModal={() => toggleModalDiscounts()} />
    </>
  );
};

export default SupportList;
